import React, { useEffect, useState, useRef } from 'react';
import { getLevelName, getHBHLevelName } from '@/constants';
import { Table, Typography, Form, Row, Col, Divider } from 'antd';
import BlockWrapper from '@/components/edit-activity-plan/block-wrapper';
import '@/components/edit-activity-plan/index.less';
import utils, { formateTimeStamp } from '@/common/utils';
import LOAD_STATUS from '@/constants/load-status';
import apis from './apis';
import batchedUpdates from '@/common/utils/batched-updates';
import './index.less';

function transformStamp2Text(value) {
  if (!value.startTime || !value.endTime) return '';
  return `${formateTimeStamp(value.startTime) || ''} 至 ${formateTimeStamp(value.endTime) || ''}`;
}

const columnsOrganization = [
  {
    title: '部门',
    dataIndex: 'organizationName',
    align: 'center',
  },
];

const columnsUser = [
  {
    title: 'erp',
    dataIndex: 'erp',
    align: 'center',
  },
];

export function useActivityInfo(activityId, _data) {
  const [data, setData] = useState(_data);
  const [loadStatus, setLoadStatus] = useState(activityId ? LOAD_STATUS.ING : LOAD_STATUS.FAILED);
  const [reload, setReload] = useState({});

  if (!activityId) console.error('?planCode= 不存在');

  const dataRef = useRef(data);

  useEffect(() => {
    let used = true;
    if (!!dataRef.current || !activityId) return;

    setLoadStatus(LOAD_STATUS.ING);

    if (!used) {
      console.log('丢弃结果');
    } else {
      apis
        .fetchActivityDetail({ planCode: activityId })
        .then((res) => {
          if (res.ret === '0') {
            batchedUpdates(() => {
              setLoadStatus(LOAD_STATUS.SUCCESS);
              setData(transformDataFromApi(res.data));
            });
          } else {
            batchedUpdates(() => {
              setLoadStatus(LOAD_STATUS.FAILED);
              setData(transformDataFromApi({}));
            });
          }
        })
        .catch((ex) => {
          console.log(ex);
        });
    }

    return () => {
      used = false;
    };
  }, [activityId, reload]);

  const refresh = useRef(function () {
    setReload({});
  });
  return [data, loadStatus, refresh.current];
}

function transformDataFromApi(data) {
  // 判断data是不是来自接口并且是对象
  if (typeof data === 'object' && data && typeof data.activityPlan === 'object' && data.activityPlan) {
    return Object.assign({}, data, data.activityPlan);
  }

  return data;
}

export default function ActivityDetail(props) {
  const { data: _data } = props;
  const data = transformDataFromApi(_data);

  useEffect(() => {
    if (!data) return;
    // 获取返回数据中的活动介绍信息，将其转为HTML挂载在指定的id上
    const activityIntroduceInfo = document.getElementById('activity-introduce-info');
    activityIntroduceInfo.innerHTML = data.remark || '';
  }, [data]);

  if (!data) return null;

  return (
    <Form component={false} labelAlign="left">
      <BlockWrapper title="活动基本信息">
        <Form.Item label="活动名称">
          <Typography.Text className="break-word">{data.activityName}</Typography.Text>
        </Form.Item>

        <Form.Item label="部门">
          <Typography.Text>{data.organizationName}</Typography.Text>
        </Form.Item>
        <Form.Item label="活动时间">
          <Typography.Text>
            {transformStamp2Text({ startTime: data.activityStartTime, endTime: data.activityEndTime })}
          </Typography.Text>
        </Form.Item>
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item label="预热期">
              <Typography.Text>
                {transformStamp2Text({ startTime: data.preStartTime, endTime: data.preEndTime })}
              </Typography.Text>
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item label="专场期">
              <Typography.Text>
                {transformStamp2Text({ startTime: data.specialStartTime, endTime: data.specialEndTime })}
              </Typography.Text>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item label="高潮期">
              <Typography.Text>
                {transformStamp2Text({ startTime: data.highStartTime, endTime: data.highEndTime })}
              </Typography.Text>
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item label="返场期">
              <Typography.Text>
                {transformStamp2Text({ startTime: data.backStartTime, endTime: data.backEndTime })}
              </Typography.Text>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="主推品类">
          <Typography.Text>{data.categoryName}</Typography.Text>
        </Form.Item>
        <Form.Item label="活动介绍">
          <Typography.Text className="break-word" id="activity-introduce-info" />
        </Form.Item>
        <Form.Item label="活动级别">
          <Typography.Text>{getLevelName(data.level, data.otherLevel)}</Typography.Text>
        </Form.Item>
        <Form.Item hidden label="货币化活动级别">
          <Typography.Text>{getHBHLevelName(data.monetizedLevel)}</Typography.Text>
        </Form.Item>
        <Form.Item label="利益点">
          <Typography.Text>
            {/* 或者其他等级 */}
            {data.interestPoint}
          </Typography.Text>
        </Form.Item>
        <Form.Item label="预估日销售额">
          <Typography.Text>{utils.thousands(utils.fen2yuan(data.dailySales))}元</Typography.Text>
        </Form.Item>
      </BlockWrapper>
      <Divider />
      <BlockWrapper title="预算信息">
        <Form.Item label="市场费用">
          <Typography.Text>
            {data.marketPrice ? utils.thousands(utils.fen2yuan(data.marketPrice)) + '元' : ''}
          </Typography.Text>
        </Form.Item>
      </BlockWrapper>
      <BlockWrapper hidden title="公开范围">
        <Form.Item label="公开范围">
          <Typography.Text>{data.openStatus === 0 ? '部分公开' : '全部公开'}</Typography.Text>
        </Form.Item>
        {data.openStatus === 0 && (
          <Form.Item
            label="可见部门"
            wrapperCol={{
              style: {
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: '450px',
              },
            }}
          >
            <Table
              rowKey="organization"
              pagination={false}
              columns={columnsOrganization}
              dataSource={data.activityPlanOpenOrganizations}
            />
          </Form.Item>
        )}
        {data.openStatus === 0 && (
          <Form.Item
            label="可见erp"
            wrapperCol={{
              style: {
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: '450px',
              },
            }}
          >
            <Table rowKey="erp" pagination={false} columns={columnsUser} dataSource={data.activityPlanOpenUsers} />
          </Form.Item>
        )}
      </BlockWrapper>
    </Form>
  );
}

export function ActivityDetailByLoad(props) {
  const [data, loadStatus] = useActivityInfo(props.activityId);

  if (loadStatus !== LOAD_STATUS.SUCCESS || !data) return null;

  return <ActivityDetail data={data} />;
}
